import { useEffect, useState } from "react";

/**
 * The ways a user can sign in, as far as the "last used" badge is concerned:
 * one of the external providers, or the email form (code or password alike).
 */
export const LAST_LOGIN_METHODS = ["google", "microsoft", "email"] as const;

export type LastLoginMethod = (typeof LAST_LOGIN_METHODS)[number];

const STORAGE_KEY = "picter:last-login-method";

/**
 * Remembers, in this browser, which method just signed the user in. Called
 * on a successful login only, never on a click: a refused or abandoned
 * attempt is not what the user "used last time".
 */
export function rememberLastLoginMethod(method: LastLoginMethod): void {
  try {
    localStorage.setItem(STORAGE_KEY, method);
  } catch {
    // storage unavailable: the badge simply does not show
  }
}

/**
 * The method this browser last signed in with, if known.
 */
export function readLastLoginMethod(): LastLoginMethod | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return (LAST_LOGIN_METHODS as readonly string[]).includes(stored ?? "")
      ? (stored as LastLoginMethod)
      : null;
  } catch {
    return null;
  }
}

/**
 * {@link readLastLoginMethod}, read after mount: the login pages are static,
 * so the first render must not depend on what only the browser knows.
 */
export function useLastLoginMethod(): LastLoginMethod | null {
  const [method, setMethod] = useState<LastLoginMethod | null>(null);

  useEffect(() => {
    setMethod(readLastLoginMethod());
  }, []);

  return method;
}
